<template>
  <div>
    <!-- Loading Spinner -->
    <div v-if="showLoader" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <!-- Table -->
    <div v-else class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="py-3 px-6"><div class="flex items-center">#</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Visit ID</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Patient Name</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Patient Unique Code</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Age</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Gender</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Queue Time</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Triage Code</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">CTAS</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Service</div></th>
            <th scope="col" class="py-3 px-6"><div class="flex items-center">Preferred Doctor</div></th>
            <th scope="col" class="py-3 px-6">Action</th>
            <th scope="col" class="py-3 px-6">Close Visit</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(visit, index) in visits" :key="index" class="border-b">
            <td>{{ index + 1 }}</td>
            <td class="px-6 py-2">{{ visit.VisitCode }}</td>
            <td class="px-6 py-2">{{ visit.patient.FullName }}</td>
            <td class="px-6 py-2">{{ visit.patient.unique_code }}</td>
            <td class="px-6 py-2">
              {{ visit.patient.Age }} {{ visit.patient.AgeType == 1 ? "Years" : "Months" }}
            </td>
            <td class="px-6 py-2">{{ visit.patient.Gender }}</td>
            <td class="px-6 py-2">{{ visit.VisitStartTime }}</td>
            <td class="px-6 py-2">
              <h1 :class="`${getTriageColor(visit.triage_glasgow_coma_scale?.score)} px-2 rounded w-3/4 text-center`">
                <span class="text-center">
                  {{ getTriageLabel(visit.triage_glasgow_coma_scale?.score) }}
                </span>
              </h1>
            </td>
            <td class="px-6 py-2">
              <span
                v-if="visit.triage_assesment?.ctas_level"
                :class="`${getCtasColor(visit.triage_assesment.ctas_level)} px-2 py-0.5 rounded text-xs font-bold`"
              >
                {{ getCtasLabel(visit.triage_assesment.ctas_level) }}
              </span>
              <span v-else class="text-gray-300 text-xs">—</span>
            </td>
            <td class="px-6 py-2">
              <span v-if="visit.consultation_service?.name">{{ visit.consultation_service.name }}</span>
              <span v-else class="text-gray-300 text-xs">—</span>
            </td>
            <td class="px-6 py-2">
              <span v-if="visit.triage_assesment?.doctor?.Username">{{ visit.triage_assesment.doctor.Username }}</span>
              <span v-else class="text-gray-300 text-xs">Not specified</span>
            </td>
            <td class="px-6 py-2">
              <button
                v-if="!visit.consultation"
                @click="handleConsultationModal(visit.VisitId)"
                class="bg-custom-yellow text-white px-4 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="checkingBalance && selectedVisitId === visit.VisitId"
              >
                <span v-if="checkingBalance && selectedVisitId === visit.VisitId">
                  <div class="animate-spin inline-block rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                  Loading...
                </span>
                <span v-else>Start Consultation</span>
              </button>
              <button
                v-else
                @click="handleConsultationModal(visit.VisitId)"
                class="bg-custom-blue text-white px-4 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="checkingBalance && selectedVisitId === visit.VisitId"
              >
                <span v-if="checkingBalance && selectedVisitId === visit.VisitId">
                  <div class="animate-spin inline-block rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                  Loading...
                </span>
                <span v-else>Continue Consultation</span>
              </button>
            </td>
            <td class="px-6 py-2">
              <CloseVisitButton :visit="visit" @closed="fetchConsultationPatients" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <PaginationComponent
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="itemsPerPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
    />

    <!-- Consultation Modal -->
    <ConsultationPatientQueueModal
      class="z-99"
      :key="modalKey"
      :show="showConsultationModal"
      :visitId="selectedVisitId"
      :state="modalState"
      @close="closeConsultationModal"
      @submit="handleAppointmentSubmit"
      @refetch="fetchConsultationPatients"
    />

    <!-- Insurance Eligible Modal -->
    <div
      v-if="showInsuranceEligible"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4">
        <div class="flex items-center mb-4">
          <svg class="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 class="text-xl font-bold text-gray-700">Insurance Coverage Available</h2>
        </div>
        <p class="text-gray-600 mb-6">
          This patient has sufficient insurance coverage for consultation. You can proceed.
        </p>
        <div class="flex justify-end gap-4">
          <button @click="proceedWithConsultation" class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
            Proceed with Consultation
          </button>
          <button @click="showInsuranceEligible = false" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Cash Payment Required Modal -->
    <div
      v-if="showCashPaymentWarning"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div class="bg-white rounded-lg shadow-md p-6 w-full max-w-md mx-4">
        <div class="flex items-center mb-4">
          <svg class="h-6 w-6 text-custom-yellow mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M12 4a8 8 0 100 16 8 8 0 000-16z" />
          </svg>
          <h2 class="text-xl font-bold text-gray-700">Payment Required</h2>
        </div>
        <p class="text-gray-600 mb-6">
          The patient needs to pay a Consultation fee of
          <span class="font-semibold">KES {{ outstandingBalance }}</span>
          before proceeding with consultation.
        </p>
        <div class="flex justify-end gap-4">
          <button @click="showCashPaymentWarning = false" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import ConsultationPatientQueueModal from "@/views/modals/ConsultationPatientQueueModal.vue";
import PaginationComponent from "@/components/shared/pagination.vue";
import CloseVisitButton from "@/components/shared/CloseVisitButton.vue";
import { formatDateTime } from "@/services/customUtilities";

export default {
  name: "ConsultationPatientQueueTab",
  components: {
    ConsultationPatientQueueModal,
    PaginationComponent,
    CloseVisitButton,
  },
  mounted() {
    this.fetchConsultationPatients();
  },
  props: {
    filters: { type: Object },
  },
  data() {
    return {
      showLoader: false,
      showConsultationModal: false,
      showCashPaymentWarning: false,
      showInsuranceEligible: false,
      checkingBalance: false,
      outstandingBalance: null,
      selectedVisitId: "",
      modalState: "create",
      modalKey: 0,
      visits: [],
      currentPage: 1,
      itemsPerPage: 25,
      totalItems: 0,
      totalPages: 0,
    };
  },
  computed: {
    getTriageColor() {
      return (triageCode) => {
        switch (triageCode) {
          case "red":    return "bg-red-500 text-white";
          case "orange": return "bg-custom-yellow text-white";
          case "yellow": return "bg-yellow-200 text-white";
          case "green":  return "bg-green-500 text-white";
          case "blue":   return "bg-blue-500 text-white";
          default:       return "";
        }
      };
    },
  },
  methods: {
    getCtasColor(level) {
      switch (String(level)) {
        case "1": return "bg-blue-600 text-white";
        case "2": return "bg-red-500 text-white";
        case "3": return "bg-yellow-400 text-yellow-900";
        case "4": return "bg-green-500 text-white";
        case "5": return "bg-gray-200 text-gray-800";
        default:  return "bg-gray-100 text-gray-500";
      }
    },
    getCtasLabel(level) {
      const labels = { "1": "I — Resuscitation", "2": "II — Emergent", "3": "III — Urgent", "4": "IV — Less Urgent", "5": "V — Non-Urgent" };
      return labels[String(level)] || "—";
    },
    async handleConsultationModal(visitId) {
      const visit = this.visits.find(v => v.VisitId === visitId);
      if (!visit) return this.$toast.error("Visit not found");

      this.selectedVisitId = visitId;
      this.checkingBalance = true;
      this.showCashPaymentWarning = false;
      this.showInsuranceEligible = false;

      try {
        // 1. Verify consultation coverage using the dedicated endpoint
        const coverageRes = await apiClient.verifyConsultationCoverage({
          patient_id: visit.patient.PatientId,
          visit_id: visitId,
        });

        if (!coverageRes.success) throw new Error("Coverage check failed");

        const coverage = coverageRes.data;

        // 2. If covered by insurance — show the insurance eligible confirmation modal
        const hasInsurance = coverage.applied_payment_methods?.some(m => m.type === "insurance");
        if (hasInsurance && coverage.is_covered) {
          this.showInsuranceEligible = true;
          return;
        }

        // 3. Covered (FFS settled) or credit billing allowed — open modal directly
        if (coverage.is_covered || coverage.settle_at_end_allowed) {
          this.modalState = visit.consultation ? "edit" : "create";
          this.modalKey++;
          this.showConsultationModal = true;
          this.$emit("closeDropdown");
          return;
        }

        // 4. Cash required — block and show balance warning
        this.outstandingBalance = parseFloat(coverage.remaining_balance || 0).toFixed(2);
        this.showCashPaymentWarning = true;

      } catch (error) {
        console.error("Coverage check error:", error);
        // Fallback: allow through if coverage check fails
        this.modalState = visit.consultation ? "edit" : "create";
        this.modalKey++;
        this.showConsultationModal = true;
        this.$emit("closeDropdown");
      } finally {
        this.checkingBalance = false;
      }
    },

    proceedWithConsultation() {
      const visit = this.visits.find(v => v.VisitId === this.selectedVisitId);
      if (visit) {
        this.showInsuranceEligible = false;
        this.modalState = visit.consultation ? "edit" : "create";
        this.modalKey++;
        this.showConsultationModal = true;
        this.$emit("closeDropdown");
      }
    },

    closeConsultationModal() {
      this.selectedVisitId = "";
      this.showConsultationModal = false;
      this.showCashPaymentWarning = false;
      this.showInsuranceEligible = false;
      this.outstandingBalance = null;
      this.checkingBalance = false;
    },

    goToPage(pageNumber) {
      this.currentPage = pageNumber;
      this.fetchConsultationPatients();
    },
    changePageSize(pageSize) {
      this.itemsPerPage = pageSize;
      this.fetchConsultationPatients();
    },
    async fetchConsultationPatients() {
      try {
        this.showLoader = true;
        const response = await apiClient.getUnifiedConsultationQueue({
          page: this.currentPage,
          per_page: this.itemsPerPage,
        });
        this.showLoader = false;
        if (response.success) {
          this.visits = response.data.map((item) => ({
            ...item,
            VisitStartTime: formatDateTime(item.VisitStartTime),
          }));
          this.totalItems = response.meta.total_items;
          this.totalPages = response.meta.total_pages;
          this.itemsPerPage = response.meta.per_page;
        } else {
          this.$toast.error("Failed to fetch consultation patients");
        }
      } catch (error) {
        this.showLoader = false;
        this.$toast.error("Failed to fetch consultation patients");
        console.log(error);
      }
    },
    async searchConsultations() {
      try {
        this.$emit("search", true);
        const response = await apiClient.searchConsultation({
          ...this.filters,
          consultation_status: 'initial',
        });
        if (response.success) {
          this.$emit("search", false);
          this.visits = response.data.map((item) => ({
            ...item,
            VisitStartTime: formatDateTime(item.VisitStartTime),
          }));
        } else {
          this.$toast.error("Failed to fetch consultation patients");
        }
      } catch (error) {
        this.$emit("search", false);
        this.$toast.error("Failed to fetch consultation patients");
        console.log(error);
      }
    },
    handleAppointmentSubmit(patientId) {
      console.log("Submitting consultation for patient ID:", patientId);
      this.closeConsultationModal();
    },
    getTriageLabel(color) {
      switch (color) {
        case "red":    return "Below 8";
        case "orange": return "9 - 13";
        case "yellow": return "14";
        case "green":  return "15";
        case "blue":   return "Code Blue";
        default:       return "";
      }
    },
  },
};
</script>